import type { GroupUser } from '~/composables/useGroups'
import type { Subscription } from '~/types/user'
import type { EphemeralMessage, QuickReply } from '~/types/messages'
import { useGroupsStore } from '~/stores/groups'

/**
 * SignalR event payloads serialize the backend's C# DTOs verbatim, so every field can arrive as
 * either camelCase or PascalCase depending on which serializer path produced it. Every handler in
 * useGroupsSignalR.ts (and the snapshot mapping in stores/groups.ts) needs the same "try both
 * spellings" lookup — centralized here instead of each call site reimplementing it.
 */
export function dualCaseString(raw: Record<string, unknown>, camelCase: string, pascalCase: string): string {
  const value = raw[camelCase] ?? raw[pascalCase]
  return value == null ? '' : String(value)
}

export function dualCaseNumber(
  raw: Record<string, unknown>,
  camelCase: string,
  pascalCase: string,
  fallback = 0,
): number {
  const value = raw[camelCase] ?? raw[pascalCase]
  return value == null ? fallback : Number(value)
}

export function dualCaseBoolean(raw: Record<string, unknown>, camelCase: string, pascalCase: string): boolean {
  return Boolean(raw[camelCase] ?? raw[pascalCase])
}

export function dualCaseStringArray(raw: Record<string, unknown>, camelCase: string, pascalCase: string): string[] {
  const value = raw[camelCase] ?? raw[pascalCase]
  return Array.isArray(value) ? value.filter((v) => v != null).map(String) : []
}

/** Maps a raw `Subscription` object (either spelling of its fields); anything that isn't an object is "no subscription". */
export function mapRawSubscription(value: unknown): Subscription | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const raw = value as Record<string, unknown>
  return {
    id: dualCaseString(raw, 'id', 'Id'),
    tier_code: dualCaseString(raw, 'tierCode', 'TierCode'),
    functions: dualCaseNumber(raw, 'functions', 'Functions'),
    activated_at: dualCaseString(raw, 'activatedAt', 'ActivatedAt') || null,
    expires_at: dualCaseString(raw, 'expiresAt', 'ExpiresAt') || null,
  }
}

/**
 * Maps a raw SignalR user payload (channel snapshots' `Users`/`users`, `UserConnected`, ...) to a
 * `GroupUser`. These payloads only ever carry the plain user roster, never role assignments —
 * `role_ids` is populated separately from the `GetGroupUsers` gRPC response — so it's always empty
 * here rather than silently omitted (a missing required field is a real type/runtime bug, not a
 * shortcut).
 */
export function mapRawGroupUser(raw: Record<string, unknown>, fallbackId = ''): GroupUser {
  return {
    id: dualCaseString(raw, 'id', 'Id') || fallbackId,
    username: dualCaseString(raw, 'username', 'Username'),
    email: dualCaseString(raw, 'email', 'Email'),
    display_name: dualCaseString(raw, 'displayName', 'DisplayName'),
    description: dualCaseString(raw, 'description', 'Description'),
    status: dualCaseString(raw, 'status', 'Status'),
    preferred_language: dualCaseString(raw, 'preferredLanguage', 'PreferredLanguage'),
    created_at: dualCaseString(raw, 'createdAt', 'CreatedAt') || undefined,
    updated_at: dualCaseString(raw, 'updatedAt', 'UpdatedAt') || undefined,
    last_activity: dualCaseString(raw, 'lastActivity', 'LastActivity') || undefined,
    is_confirmed: dualCaseBoolean(raw, 'isConfirmed', 'IsConfirmed'),
    is_bot: dualCaseBoolean(raw, 'isBot', 'IsBot'),
    subscription: mapRawSubscription(raw['subscription'] ?? raw['Subscription']),
    avatar_url: dualCaseString(raw, 'avatarUrl', 'AvatarUrl'),
    banner_url: dualCaseString(raw, 'bannerUrl', 'BannerUrl'),
    user_status: dualCaseNumber(raw, 'userStatus', 'UserStatus'),
    role_ids: [],
  }
}

/**
 * Maps a raw SignalR channel-member entry (`Channel.Users`/`users` in group snapshot events like
 * `ChannelCreated`) to a `GroupUser`. Unlike the plain roster entries `mapRawGroupUser` handles,
 * these wrap the user under a nested `User`/`user` key alongside sibling `MicIsEnabled`/
 * `CamIsEnabled`/`IsDeafened` flags (mirroring the backend's `ChannelVoiceUser`) — passing the
 * wrapper straight to `mapRawGroupUser` finds none of the user fields at the top level and
 * silently produces an all-blank user (rendered as "Неизвестный пользователь").
 */
export function mapRawChannelUser(raw: Record<string, unknown>): GroupUser {
  const nestedUser = (raw.user ?? raw.User ?? raw) as Record<string, unknown>
  return {
    ...mapRawGroupUser(nestedUser),
    mic_is_enabled: dualCaseBoolean(raw, 'micIsEnabled', 'MicIsEnabled'),
    cam_is_enabled: dualCaseBoolean(raw, 'camIsEnabled', 'CamIsEnabled'),
    is_deafened: dualCaseBoolean(raw, 'isDeafened', 'IsDeafened'),
  }
}

/**
 * Applies a `UserProfileUpdated` payload to every cached copy of that user across the groups store.
 * The backend broadcasts this on both the groups hub (`group-events`) and the users hub
 * (`user-events`) — same as `UserStatusChanged` — so both `useGroupsSignalR.ts` and
 * `useUsersSignalR.ts` call this rather than each reimplementing (and risking drifting) the same
 * parsing/refresh logic. Applying it twice for one real change is harmless: both calls converge on
 * the same field values.
 */
export function applyUserProfileUpdated(event: { userId: string; payloadJson: string }) {
  const groupsStore = useGroupsStore()
  const payload = JSON.parse(event.payloadJson) as Record<string, unknown>
  const userId = dualCaseString(payload, 'userId', 'UserId') || event.userId
  // The backend now sends the ready-to-use URL directly (no more avatarId -> GetUserAvatar
  // round trip) — patching `avatar_url` straight into the store is enough on its own to make the
  // avatar change everywhere it's rendered: every `<img>` resolves through `resolveMediaUrl`,
  // which re-triggers a disk-cache lookup/download whenever the URL it's given changes.
  // Present-but-empty means "removed / hidden" (deleted, or the subscription behind it lapsed) and must
  // clear the cached URL; only a payload without the key at all leaves the cached one alone.
  const hasKey = (camel: string, pascal: string) => camel in payload || pascal in payload
  const avatarUrl = hasKey('avatarUrl', 'AvatarUrl') ? dualCaseString(payload, 'avatarUrl', 'AvatarUrl') : undefined
  const bannerUrl = hasKey('bannerUrl', 'BannerUrl') ? dualCaseString(payload, 'bannerUrl', 'BannerUrl') : undefined
  const displayName = dualCaseString(payload, 'displayName', 'DisplayName')
  const description = dualCaseString(payload, 'description', 'Description')
  const status = dualCaseString(payload, 'status', 'Status')
  // Absent means "unchanged": defaulting to 0 would mark the user ONLINE on any profile edit.
  const presenceStatus = hasKey('presenceStatus', 'PresenceStatus')
    ? dualCaseNumber(payload, 'presenceStatus', 'PresenceStatus', NaN)
    : undefined
  const lastActivity = hasKey('lastActivity', 'LastActivity')
    ? dualCaseString(payload, 'lastActivity', 'LastActivity')
    : undefined

  if (!userId) return

  groupsStore.setUser(userId, {
    avatarUrl,
    bannerUrl,
    displayName,
    description,
    status,
    presenceStatus,
    lastActivity,
  })
}

/** `EphemeralMessageCreated` payload (`EphemeralMessageEvent`, user-events) → the store's shape; null if it names no channel. */
export function parseEphemeralMessage(raw: Record<string, unknown>): Omit<EphemeralMessage, 'anchor_id'> | null {
  const channelId = dualCaseString(raw, 'channelId', 'ChannelId')
  if (!channelId) return null
  const rawReplies = raw.quickReplies ?? raw.QuickReplies
  const quickReplies: QuickReply[] = Array.isArray(rawReplies)
    ? rawReplies
        .map((r) => r as Record<string, unknown>)
        .map((r) => ({ label: dualCaseString(r, 'label', 'Label'), recommended: dualCaseBoolean(r, 'recommended', 'Recommended') }))
        .filter((r) => r.label)
    : []
  return {
    id: dualCaseString(raw, 'id', 'Id') || crypto.randomUUID(),
    channel_id: channelId,
    author_id: dualCaseString(raw, 'authorId', 'AuthorId'),
    content: dualCaseString(raw, 'content', 'Content') || null,
    reply_to: dualCaseString(raw, 'replyTo', 'ReplyTo') || null,
    quick_replies: quickReplies,
    components_json: dualCaseString(raw, 'componentsJson', 'ComponentsJson') || null,
    created_at: dualCaseString(raw, 'createdAt', 'CreatedAt') || new Date().toISOString(),
  }
}
